import React, { useState, useEffect } from "react";
import Layout from "../pages/Layout";
import Advertise from "../pages/Advertise";

const EnvironmentalImpact = () => {
  // State variables for Environmental Impact calculation
  const [emissions, setEmissions] = useState(0); // Total emissions from activities (in CO2 equivalent)
  const [waterUsage, setWaterUsage] = useState(0); // Total water usage (in liters)
  const [wasteProduction, setWasteProduction] = useState(0); // Total waste production (in kg)
  const [impactResult, setImpactResult] = useState(0); // Calculated environmental impact

  // Event handlers to update state values
  const handleEmissionsChange = (e) =>
    setEmissions(parseFloat(e.target.value) || 0);
  const handleWaterUsageChange = (e) =>
    setWaterUsage(parseFloat(e.target.value) || 0);
  const handleWasteProductionChange = (e) =>
    setWasteProduction(parseFloat(e.target.value) || 0);

  // useEffect hook to calculate environmental impact when any input changes
  useEffect(() => {
    // A simple example: impact calculation could be weighted based on emissions, water usage, and waste production
    const calculatedImpact =
      emissions * 0.5 + waterUsage * 0.3 + wasteProduction * 0.2; // Example formula for total environmental impact
    setImpactResult(calculatedImpact);

    // This will initialize or reinitialize all popovers on the page whenever the component renders
    const popoverTriggerList = [].slice.call(
      document.querySelectorAll('[data-bs-toggle="popover"]')
    );

    // Clean up any existing popover instances
    popoverTriggerList.forEach((popoverTriggerEl) => {
      const popover = window.bootstrap.Popover.getInstance(popoverTriggerEl); // Check if instance already exists
      if (popover) {
        popover.dispose(); // Dispose of existing popover
      }
    });

    // Re-initialize popovers
    popoverTriggerList.forEach((popoverTriggerEl) => {
      new window.bootstrap.Popover(popoverTriggerEl); // Re-initialize popovers
    });
  }, [emissions, waterUsage, wasteProduction]);

  return (
    <Layout title="Environmental Impact Calculator" path="environmental-impact">
      <section className="section-padding" id="impact-calculator">
        <div className="container mt-5">
          <div className="row">
            {/* Environmental Impact Calculator Form */}
            <div className="col-lg-9 col-md-8 mb-4">
              <div
                className="p-4 border rounded shadow"
                style={{ border: "1px solid black" }}
              >
                <h3 className="text-center mb-4">
                  Environmental Impact Calculator
                </h3>
                <div className="container">
                  <div className="row gy-3">
                    {/* Emissions Input */}
                    <div className="col-md-6 col-lg-4">
                      <label htmlFor="emissions" className="form-label">
                        Emissions (CO2 equivalent in kg)
                        <span
                          className="ms-2 text-muted"
                          data-bs-toggle="popover"
                          data-bs-html="true"
                          data-bs-placement="top"
                          data-bs-content="<strong>Emissions:</strong> Enter the amount of CO2 equivalent emissions produced in kilograms."
                          style={{ cursor: "pointer" }}
                        >
                          <i className="bi bi-info-circle"></i>
                        </span>
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        id="emissions"
                        placeholder="Enter emissions in kg"
                        value={emissions || ""}
                        onChange={handleEmissionsChange}
                      />
                    </div>

                    {/* Water Usage Input */}
                    <div className="col-md-6 col-lg-4">
                      <label htmlFor="waterUsage" className="form-label">
                        Water Usage (liters)
                        <span
                          className="ms-2 text-muted"
                          data-bs-toggle="popover"
                          data-bs-html="true"
                          data-bs-placement="top"
                          data-bs-content="<strong>Water Usage:</strong> Enter the total amount of water used in liters."
                          style={{ cursor: "pointer" }}
                        >
                          <i className="bi bi-info-circle"></i>
                        </span>
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        id="waterUsage"
                        placeholder="Enter water usage in liters"
                        value={waterUsage || ""}
                        onChange={handleWaterUsageChange}
                      />
                    </div>

                    {/* Waste Production Input */}
                    <div className="col-md-6 col-lg-4">
                      <label htmlFor="wasteProduction" className="form-label">
                        Waste Production (kg)
                        <span
                          className="ms-2 text-muted"
                          data-bs-toggle="popover"
                          data-bs-html="true"
                          data-bs-placement="top"
                          data-bs-content="<strong>Waste Production:</strong> Enter the amount of waste produced in kilograms."
                          style={{ cursor: "pointer" }}
                        >
                          <i className="bi bi-info-circle"></i>
                        </span>
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        id="wasteProduction"
                        placeholder="Enter waste production in kg"
                        value={wasteProduction || ""}
                        onChange={handleWasteProductionChange}
                      />
                    </div>
                  </div>

                  {/* Result Section */}
                  <div className="mt-4">
                    <div className="p-3 text-center">
                      <h6>
                        Environmental Impact Score:{" "}
                        <span className="fw-bold ms-3">
                          {impactResult === 0
                            ? "0.00"
                            : impactResult.toLocaleString(undefined, {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              })}
                        </span>
                      </h6>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Advertisement Section */}
            <Advertise />
          </div>
        </div>
      </section>

      {/* Introduction Section */}
      <section className="introduction-section" id="introduction">
        <div className="container">
          <div className="row">
            <div className="col-lg-12">
              <div className="p-5">
                <h3 className="text-center mb-5">
                  Introduction to the Environmental Impact Calculator
                </h3>

                {/* What is an Environmental Impact Calculator? */}
                <h4 className="mt-3 mb-3">
                  What is an Environmental Impact Calculator?
                </h4>
                <p>
                  The **Environmental Impact Calculator** helps individuals and
                  businesses estimate their environmental footprint. By
                  inputting key factors like emissions, water usage, and waste
                  production, the tool provides a total environmental impact
                  score.
                </p>

                {/* Why Use an Environmental Impact Calculator? */}
                <h4 className="mt-4 mb-3">
                  Why Use an Environmental Impact Calculator?
                </h4>
                <p>
                  Using this calculator allows businesses and individuals to
                  track their sustainability goals and identify areas where they
                  can reduce their environmental impact. It encourages
                  responsible environmental practices and supports eco-friendly
                  decisions.
                </p>

                {/* Benefits of Using an Environmental Impact Calculator */}
                <h4 className="mt-4 mb-3">
                  Benefits of Using an Environmental Impact Calculator
                </h4>
                <ul>
                  <li>
                    <strong>Supports sustainable practices:</strong> Helps track
                    and reduce carbon footprint, water usage, and waste
                    production.
                  </li>
                  <li>
                    <strong>Encourages eco-friendly decisions:</strong> Provides
                    data to make environmentally conscious decisions.
                  </li>
                  <li>
                    <strong>
                      Supports corporate responsibility reporting:
                    </strong>{" "}
                    Aids in reporting environmental impact to stakeholders and
                    regulators.
                  </li>
                </ul>

                {/* Suitable For */}
                <h4 className="mt-4 mb-3">
                  Who Should Use an Environmental Impact Calculator?
                </h4>
                <p>The Environmental Impact Calculator is suitable for:</p>
                <ul>
                  <li>
                    <strong>Businesses with sustainability goals:</strong> To
                    track and reduce their environmental impact.
                  </li>
                  <li>
                    <strong>Environmental NGOs:</strong> To support research and
                    advocacy in reducing environmental footprints.
                  </li>
                  <li>
                    <strong>Individuals:</strong> To monitor and minimize their
                    personal environmental impact.
                  </li>
                </ul>

                {/* Additional Questions */}
                <h4 className="mt-4 mb-3">
                  How Can I Reduce My Environmental Impact?
                </h4>
                <p>
                  Reducing your environmental impact can be done by adopting
                  energy-efficient technologies, reducing waste, recycling, and
                  conserving water. Being mindful of your emissions and the
                  products you use is crucial to reducing your footprint.
                </p>

                <h4 className="mt-4 mb-3">
                  Why Should Businesses Track Their Environmental Impact?
                </h4>
                <p>
                  Businesses track their environmental impact to comply with
                  regulations, improve their sustainability performance, enhance
                  brand reputation, and meet the expectations of
                  environmentally-conscious customers and stakeholders.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default EnvironmentalImpact;
